import { NetworkLogo, TOKEN_LOGO } from '@app/collections/transactions/tableLogos'
import { Button, Col, Row, Spin, Table, Tag } from '@app/components'
import { LeftOutlined, RightOutlined } from '@app/components/icons'
import { IStore } from '@app/redux'
import { auth, Axios, fetchPaginatedDocs } from '@app/services'
import { EFirestoreCollection } from '@app/services/shared'
import { getTagColor, toCurrency, toSmallerCurrency } from '@app/utils'
import { ColumnsType } from 'antd/es/table'
import { where } from 'firebase/firestore'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { CoastIcon, HexIcon, PulseChainIcon, PulseXIcon, WaitV2Icon } from '~public'
import { GloryIcon } from '~public/icons/GloryIcon'
import { IncIcon } from '~public/icons/IncIcon'
import { PHLPv2Icon } from '~public/icons/PHLPv2Icon'
import { ProveXIcon } from '~public/icons/ProveXIcon'
import { TideIcon } from '~public/icons/TideIcon'

export const Transaction: React.FC<any> = (transactionRefresh: number) => {
  const { user } = useSelector((state: IStore) => state)

  const [transactions, setTransactions] = useState<{ nodes: any[]; countPrev: number; countNext }>({
    nodes: [],
    countPrev: 0,
    countNext: 0,
  })
  const [pagination, setPagination] = useState<{
    startAfter?: any
    endBefore?: any
  }>({ startAfter: null, endBefore: null })

  const [loading, setLoading] = useState(false)
  //const [firstLoad, setFirstLoad] = useState(true)
  //const [transactionId, setTransactionId] = useState<string | undefined>(undefined)

  useEffect(() => {
    fetchTransactions()
  }, [pagination, transactionRefresh])

  const fetchTransactions = async () => {
    setLoading(true)
    const paginatedInput = {
      collection: EFirestoreCollection.TRANSACTIONS,
      limit: 10,
      startAfter: pagination.startAfter,
      endBefore: pagination.endBefore,
    }

    const response = await fetchPaginatedDocs(
      paginatedInput,
      where('userId', '==', user.uid),
      where('type', '==', 'Sell'),
    )

    response.nodes.forEach((item) => {
      if (item.type == 'Deposit') {
        item.status =
          item.status == 'Failed'
            ? 'Failed'
            : item.status == 'Confirmed'
              ? 'Confirmed'
              : item.status == 'Pending'
                ? 'Sent'
                : 'Pending'
      }
    })

    response.nodes = response.nodes.filter((item) => {
      if (user.selectedEntityName) {
        return item.custodialId != undefined && item.custodialId == user.selectedEntityIdentityId
      } else {
        return (
          item.custodialId == user.fortressCustodialId || item.custodialId == undefined || item.custodialId == user.uid
        )
      }
    })

    //setTransactionId(response.nodes[0]?.id)

    setTransactions(response)
    setLoading(false)

    const queued = response.nodes.filter(
      (item) => item.engine === 'eth-v2' && item.sendStatus === 'queued' && item.hash,
    )
    if (queued.length) {
      try {
        const authorization = await auth.currentUser.getIdToken()
        await Promise.all(
          queued.map((item) =>
            Axios.post('/api/ethereum/sell', { transactionId: item.id }, { headers: { Authorization: authorization } }),
          ),
        )
        const refreshed = await fetchPaginatedDocs(
          paginatedInput,
          where('userId', '==', user.uid),
          where('type', '==', 'Sell'),
        )
        refreshed.nodes = refreshed.nodes.filter((item) => {
          if (user.selectedEntityName) {
            return item.custodialId != undefined && item.custodialId == user.selectedEntityIdentityId
          }
          return (
            item.custodialId == user.fortressCustodialId ||
            item.custodialId == undefined ||
            item.custodialId == user.uid
          )
        })
        setTransactions(refreshed)
      } catch {
        // settle retry is best-effort; table still shows queued
      }
    }
  }

  const columns: ColumnsType = [
    {
      dataIndex: 'date',
      title: 'Time',
      render: (item) => `${moment.unix(item).format('DD/MM/YY')} ${moment.unix(item).format('HH:mm')}`,
    },
    {
      dataIndex: 'amountSent',
      title: 'Pay',
      render: (item) => `${toCurrency(parseFloat(item), false)}`,
    },
    {
      dataIndex: 'amountReceived',
      title: 'Receive',
      render: (item) => `${toCurrency(parseFloat(item), true)}`,
    },
    {
      dataIndex: 'amountSent',
      title: 'Token',
      render: (item) =>
        item.split(' ')[1] == 'PLS' || item.split(' ')[1] == 'wPLS' ? (
          <PulseChainIcon style={TOKEN_LOGO} />
        ) : item.split(' ')[1] == 'PLSX' ? (
          <PulseXIcon style={TOKEN_LOGO} />
        ) : item.split(' ')[1] == 'HEX' || item.split(' ')[1] == 'eHEX' ? (
          <HexIcon style={TOKEN_LOGO} />
        ) : item.split(' ')[1] == 'WAIT' ? (
          <WaitV2Icon style={TOKEN_LOGO} />
        ) : item.split(' ')[1] == 'INC' ? (
          <IncIcon style={TOKEN_LOGO} />
        ) : item.split(' ')[1] == 'PHLPv2' ? (
          <PHLPv2Icon style={TOKEN_LOGO} />
        ) : item.split(' ')[1] == 'TIDE' ? (
          <TideIcon style={TOKEN_LOGO} />
        ) : item.split(' ')[1] == 'GLORY' ? (
          <GloryIcon style={TOKEN_LOGO} />
        ) : item.split(' ')[1] == 'CST' ? (
          <CoastIcon style={TOKEN_LOGO} />
        ) : item.split(' ')[1] == 'PRVX' ? (
          <ProveXIcon style={TOKEN_LOGO} />
        ) : (
          <></>
        ),
    },
    {
      dataIndex: 'engine',
      title: 'Network',
      render: (_item, row: any) => {
        const isEth = row.engine === 'eth-v2' || row.chainId === 1 || row.tokenCode === 'eHEX'
        return <NetworkLogo isEth={isEth} />
      },
    },
    {
      dataIndex: 'price',
      title: 'Price',
      render: (item, row: any) => {
        const qty = parseFloat(row.hexAmount || row.amountSent)
        const usd = row.usdCredited ?? row.usdQuoted ?? parseFloat(row.amountReceived)
        const price = item ?? (qty > 0 && Number.isFinite(usd) ? usd / qty : undefined)
        return price == undefined || !Number.isFinite(price) ? <></> : `${toSmallerCurrency(price, true)}`
      },
    },
    {
      dataIndex: 'buyStep',
      title: 'Trade',
      render: (item, row: any) => {
        if (row.engine === 'eth-v2') {
          if (row.sendStatus === 'confirmed') return <Tag color={getTagColor('Confirmed')}>Confirmed</Tag>
          if (row.sendStatus === 'failed') return <Tag color={getTagColor('Failed')}>Failed</Tag>
          return <Tag color={getTagColor('Enqueued')}>Queued</Tag>
        }
        return item < 3 ? <Spin size="small" /> : <Tag color={getTagColor('Confirmed')}>Confirmed</Tag>
      },
    },
    {
      dataIndex: 'buyStep',
      title: 'Send',
      render: (item, row: any) => {
        if (row.engine === 'eth-v2') {
          if (row.sendStatus === 'confirmed') return <Tag color={getTagColor('Confirmed')}>Confirmed</Tag>
          if (row.sendStatus === 'failed') return <Tag color={getTagColor('Failed')}>Failed</Tag>
          return <></>
        }
        return item < 3 ? <></> : <Tag color={getTagColor('Confirmed')}>Confirmed</Tag>
      },
    },
  ]

  return (
    <Row>
      <Col span={24}>
        <Table
          loading={false}
          columns={columns}
          dataSource={transactions.nodes}
          scroll={{ x: true }}
          pagination={false}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div>
            <Button
              type="text"
              onClick={() => setPagination({ endBefore: transactions.nodes[0] })}
              disabled={transactions.countPrev === 0 || loading}
            >
              <LeftOutlined />
            </Button>
            <Button
              type="text"
              onClick={() => setPagination({ startAfter: transactions.nodes[transactions.nodes.length - 1] })}
              disabled={transactions.countNext === 0 || loading}
            >
              <RightOutlined />
            </Button>
          </div>
        </div>
      </Col>
    </Row>
  )
}
